import { motion } from "framer-motion";
import { Coffee, ShieldCheck, Tv, PhoneCall, Usb, Route } from "lucide-react";
import { JR_BENEFITS, JR_PHOTOS, JR_DECK_PHOTOS } from "./juniorData";

export const JuniorDetails = () => (
  <section className="py-20 md:py-28 bg-dream-surface" data-testid="jr-details">
    <div className="max-w-6xl mx-auto px-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-16" data-testid="jr-deck-gallery">
        {JR_DECK_PHOTOS.map((src, i) => (
          <motion.div key={src} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }} className={`aspect-[4/5] rounded-2xl overflow-hidden border-2 ${["border-lime-300", "border-pink-400", "border-cyan-300", "border-yellow-300"][i]}`}>
            <img src={src} alt="Pioneer DJ decks in the Dream Makers DJ Room" loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
          </motion.div>
        ))}
      </div>
      <p className="font-mono text-xs tracking-[0.3em] uppercase text-cyan-300 mb-4">More information</p>
      <h2 className="font-display text-3xl sm:text-4xl font-bold text-white tracking-tight mb-12">What&apos;s in each package.</h2>
      <div className="grid md:grid-cols-2 gap-6">
        <div className="rounded-3xl p-8 bg-white/[0.03] border border-cyan-400/30">
          <p className="text-cyan-300 font-display text-xl font-bold">Casual Private Lesson · $150 <span className="text-white/50 text-base font-normal">(1.5 hours)</span></p>
          <ul className="mt-5 space-y-3 text-white/80">
            <li className="flex gap-3"><Route className="w-5 h-5 text-cyan-300 flex-shrink-0" /> 90 minutes one-on-one with a coach on club-standard Pioneer gear</li>
            <li className="flex gap-3"><Usb className="w-5 h-5 text-cyan-300 flex-shrink-0" /> A USB loaded with our music, so they can start mixing straight away</li>
            <li className="flex gap-3"><ShieldCheck className="w-5 h-5 text-cyan-300 flex-shrink-0" /> Real beatmatching, mixing and transitions from day one</li>
          </ul>
        </div>
        <div className="rounded-3xl p-8 bg-gradient-to-br from-lime-300/[0.07] to-pink-500/[0.07] border border-lime-300/40">
          <p className="text-lime-300 font-display text-xl font-bold">4-Week DJ Starter · $500 <span className="text-white/50 text-base font-normal">(6 hours) · our best value</span></p>
          <ul className="mt-5 space-y-3 text-white/80">
            <li className="flex gap-3"><Route className="w-5 h-5 text-lime-300 flex-shrink-0" /> Four sessions built around each student</li>
            <li className="flex gap-3"><Usb className="w-5 h-5 text-lime-300 flex-shrink-0" /> Students pick their favourite songs and genres beforehand, and we load them onto their own USB</li>
            <li className="flex gap-3"><ShieldCheck className="w-5 h-5 text-lime-300 flex-shrink-0" /> A personalised learning pathway to track their progress</li>
            <li className="flex gap-3"><Coffee className="w-5 h-5 text-lime-300 flex-shrink-0" /> A free extra hour of room hire to practise</li>
          </ul>
        </div>
      </div>
    </div>
  </section>
);

export const JuniorWhy = () => (
  <section className="relative py-20 md:py-28 bg-gradient-to-b from-fuchsia-950 via-purple-950 to-dream-bg overflow-hidden" data-testid="jr-why">
    <div className="absolute -top-20 right-0 w-[500px] h-[500px] bg-pink-500/10 rounded-full blur-3xl pointer-events-none" />
    <div className="relative max-w-6xl mx-auto px-6">
      <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-12 items-end mb-14">
        <div>
          <p className="font-mono text-xs tracking-[0.3em] uppercase text-pink-400 mb-4">Why music matters</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight leading-[1.05]">
            More than a hobby. <span className="font-serif italic font-normal text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-lime-300">It&apos;s an outlet.</span>
          </h2>
        </div>
        <p className="text-white/65 leading-relaxed">
          We know the holidays can be a tough time for some young people: long days, less routine and more time alone on screens. Music is a great outlet. Learning to DJ builds confidence and focus, and gives kids a positive way to express themselves and look after their mental health.
        </p>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {JR_BENEFITS.map(({ icon: Icon, title, text }, i) => (
          <motion.div
            key={title}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
            whileHover={{ y: -4 }}
            className="p-7 rounded-3xl bg-white/[0.06] border border-white/15 hover:border-lime-300/60 transition-colors backdrop-blur-sm"
            data-testid={`jr-benefit-${i}`}
          >
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-lime-300 to-pink-400 flex items-center justify-center mb-5">
              <Icon className="w-6 h-6 text-black" />
            </div>
            <h3 className="font-display text-lg font-bold text-white">{title}</h3>
            <p className="text-white/60 text-sm mt-2 leading-relaxed">{text}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const PARENT_POINTS = [
  { icon: ShieldCheck, title: "WWCC & police checked", text: "Every coach holds a current Working With Children Check and police check." },
  { icon: Coffee, title: "Parent lounge", text: "Grab a free coffee, watch TV and relax while your child learns." },
  { icon: PhoneCall, title: "We call you first", text: "After booking, we call to chat about your child and collect their favourite songs." },
  { icon: Tv, title: "Track their progress", text: "Starter students follow a personalised pathway, so you can see how they're growing." },
];

export const JuniorParents = () => (
  <section className="py-20 md:py-28 bg-dream-surface" data-testid="jr-parents">
    <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
      <div className="relative rounded-3xl overflow-hidden aspect-[4/3] border border-white/10">
        <img src={JR_PHOTOS.lounge} alt="Dream Makers lounge area" loading="lazy" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
        <span className="absolute bottom-5 left-5 px-4 py-2 rounded-full bg-cyan-300 text-black text-sm font-bold">Parents welcome. Coffee&apos;s on us.</span>
      </div>
      <div>
        <p className="font-mono text-xs tracking-[0.3em] uppercase text-cyan-300 mb-4">For parents</p>
        <h2 className="font-display text-3xl sm:text-4xl font-bold text-white tracking-tight">Safe, supported &amp; fun.</h2>
        <p className="text-white/60 mt-4">Every student is supported, encouraged and made to feel like they belong.</p>
        <div className="grid sm:grid-cols-2 gap-4 mt-8">
          {PARENT_POINTS.map(({ icon: Icon, title, text }) => (
            <div key={title} className="p-5 rounded-2xl bg-white/[0.03] border border-white/10">
              <Icon className="w-5 h-5 text-cyan-300 mb-3" />
              <p className="text-white font-semibold text-sm">{title}</p>
              <p className="text-white/55 text-sm mt-1">{text}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);
